import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap, fromEvent, merge, timer, Subject } from 'rxjs';
import { Router } from '@angular/router';
import { takeUntil, throttleTime } from 'rxjs/operators';
import { PublicClientApplication, AuthenticationResult } from '@azure/msal-browser';
import { User, LoginRequest, LoginResponse, AuthState } from '../models/user.model';
import { IMC_AUTH_CONFIG, IMCAuthConfig } from '../auth.config';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly IDLE_TIMEOUT: number;
  private readonly ACTIVITY_CHECK_INTERVAL = 60 * 1000;
  private readonly STORAGE_PREFIX: string;

  private authStateSubject = new BehaviorSubject<AuthState>({
    user: null,
    token: null,
    isAuthenticated: false
  });

  public authState$ = this.authStateSubject.asObservable();
  private idleTimer: any;
  private destroy$ = new Subject<void>();

  private msalInstance: PublicClientApplication | null = null;

  // Tracks if we're actively processing an Azure AD redirect (backend auth in progress)
  private azureRedirectProcessing = new BehaviorSubject<boolean>(false);
  public azureRedirectProcessing$ = this.azureRedirectProcessing.asObservable();

  // Tracks if MSAL initialization is complete (redirect check done)
  private msalInitialized = new BehaviorSubject<boolean>(false);
  public msalInitialized$ = this.msalInitialized.asObservable();

  constructor(
    private http: HttpClient,
    private router: Router,
    @Inject(IMC_AUTH_CONFIG) private config: IMCAuthConfig
  ) {
    this.IDLE_TIMEOUT = (config.sessionTimeoutMinutes || 120) * 60 * 1000;
    this.STORAGE_PREFIX = config.storagePrefix || 'callavoidance_auth';

    if (config.azureAD) {
      this.initializeMSAL();
    } else {
      // No Azure AD configured, mark as initialized
      this.msalInitialized.next(true);
    }

    this.loadAuthState();
    this.initializeIdleTimeout();
  }

  private async initializeMSAL(): Promise<void> {
    if (!this.config.azureAD) return;

    const msalConfig = {
      auth: {
        clientId: this.config.azureAD.clientId,
        authority: `https://login.microsoftonline.com/${this.config.azureAD.tenantId}`,
        redirectUri: this.config.azureAD.redirectUri || window.location.origin,
        postLogoutRedirectUri: this.config.azureAD.postLogoutRedirectUri || window.location.origin,
        navigateToLoginRequestUrl: false
      },
      cache: {
        cacheLocation: 'localStorage' as const,
        storeAuthStateInCookie: false
      }
    };

    this.msalInstance = new PublicClientApplication(msalConfig);
    await this.msalInstance.initialize();
    this.handleAzureADRedirect();
  }

  private loadAuthState(): void {
    const token = localStorage.getItem(`${this.STORAGE_PREFIX}_token`);
    const userStr = localStorage.getItem(`${this.STORAGE_PREFIX}_user`);
    const lastActivity = localStorage.getItem(`${this.STORAGE_PREFIX}_last_activity`);

    if (token && userStr) {
      try {
        const user = JSON.parse(userStr);
        const now = Date.now();
        const lastActivityTime = lastActivity ? parseInt(lastActivity, 10) : now;

        if (now - lastActivityTime > this.IDLE_TIMEOUT) {
          console.log('Session expired due to inactivity');
          this.clearAuthState();
          this.router.navigate([this.config.loginRoute || '/login']);
          return;
        }

        this.authStateSubject.next({
          user,
          token,
          isAuthenticated: true
        });
        this.updateLastActivity();
      } catch (error) {
        this.clearAuthState();
      }
    }
  }

  private initializeIdleTimeout(): void {
    if (!this.isAuthenticated()) {
      return;
    }

    const events = [
      fromEvent(document, 'click'),
      fromEvent(document, 'keypress'),
      fromEvent(document, 'scroll'),
      fromEvent(document, 'mousemove'),
      fromEvent(document, 'touchstart')
    ];

    merge(...events)
      .pipe(
        throttleTime(30000),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (this.isAuthenticated()) {
          this.updateLastActivity();
          this.resetIdleTimer();
        }
      });

    this.resetIdleTimer();

    timer(this.ACTIVITY_CHECK_INTERVAL, this.ACTIVITY_CHECK_INTERVAL)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.checkIdleTimeout();
      });
  }

  private updateLastActivity(): void {
    localStorage.setItem(`${this.STORAGE_PREFIX}_last_activity`, Date.now().toString());
  }

  private resetIdleTimer(): void {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
    }

    this.idleTimer = setTimeout(() => {
      if (this.isAuthenticated()) {
        console.log('Session expired due to inactivity');
        this.logout();
        this.router.navigate([this.config.loginRoute || '/login'], {
          queryParams: { expired: 'true' }
        });
      }
    }, this.IDLE_TIMEOUT);
  }

  private checkIdleTimeout(): void {
    const lastActivity = localStorage.getItem(`${this.STORAGE_PREFIX}_last_activity`);
    if (lastActivity && this.isAuthenticated()) {
      const now = Date.now();
      const lastActivityTime = parseInt(lastActivity, 10);

      if (now - lastActivityTime > this.IDLE_TIMEOUT) {
        console.log('Session expired due to inactivity check');
        this.logout();
        this.router.navigate([this.config.loginRoute || '/login'], {
          queryParams: { expired: 'true' }
        });
      }
    }
  }

  login(credentials: LoginRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.config.apiUrl}/auth/login`, credentials)
      .pipe(
        tap(response => {
          this.setAuthState(response.user, response.token);
          this.initializeIdleTimeout();
        })
      );
  }

  logout(): void {
    this.clearAuthState();
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
    }
    this.router.navigate([this.config.loginRoute || '/']);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
    }
  }

  private setAuthState(user: User, token: string): void {
    localStorage.setItem(`${this.STORAGE_PREFIX}_token`, token);
    localStorage.setItem(`${this.STORAGE_PREFIX}_user`, JSON.stringify(user));
    this.updateLastActivity();

    this.authStateSubject.next({
      user,
      token,
      isAuthenticated: true
    });
  }

  private clearAuthState(): void {
    localStorage.removeItem(`${this.STORAGE_PREFIX}_token`);
    localStorage.removeItem(`${this.STORAGE_PREFIX}_user`);
    localStorage.removeItem(`${this.STORAGE_PREFIX}_last_activity`);

    this.authStateSubject.next({
      user: null,
      token: null,
      isAuthenticated: false
    });
  }

  getToken(): string | null {
    return localStorage.getItem(`${this.STORAGE_PREFIX}_token`);
  }

  isAuthenticated(): boolean {
    return this.authStateSubject.value.isAuthenticated;
  }

  getCurrentUser(): User | null {
    return this.authStateSubject.value.user;
  }

  isAdmin(): boolean {
    const user = this.getCurrentUser();
    return user?.isAdmin || false;
  }

  hasGroup(groupName: string): boolean {
    const user = this.getCurrentUser();
    if (!user) return false;
    if (user.isAdmin) return true; // Admins have access to all groups
    return user.groups?.includes(groupName) || false;
  }

  /**
   * Azure AD Authentication Methods
   */

  loginWithAzureAD(): Observable<LoginResponse> {
    if (!this.msalInstance) {
      console.error('[Azure AD] MSAL not initialized');
      return new Observable<LoginResponse>();
    }

    console.log('[Azure AD] Initiating redirect login');
    const loginRequest = {
      scopes: ['openid', 'profile', 'email', 'User.Read'],
      extraQueryParameters: {
        claims: JSON.stringify({
          id_token: {
            groups: null
          }
        })
      }
    };

    this.msalInstance.loginRedirect(loginRequest);

    return new Observable<LoginResponse>();
  }

  private handleAzureADRedirect(): void {
    if (!this.msalInstance) {
      this.msalInitialized.next(true);
      return;
    }

    console.log('[Azure AD] Checking for redirect response...');
    // Set processing to true before checking - guard will wait for this
    this.azureRedirectProcessing.next(true);

    this.msalInstance.handleRedirectPromise()
      .then((authResult: AuthenticationResult | null) => {
        if (authResult) {
          console.log('[Azure AD] Redirect authentication successful', authResult);

          this.http.post<LoginResponse>(
            `${this.config.apiUrl}/auth/login`,
            { azureToken: authResult.idToken }
          ).subscribe({
            next: (response) => {
              console.log('[Azure AD] Backend authentication successful, user:', response.user);
              this.setAuthState(response.user, response.token);
              this.initializeIdleTimeout();

              if (this.config.requireAdmin && !response.user.isAdmin) {
                console.error('[Azure AD] User is not an admin');
                this.clearAuthState();
                this.msalInitialized.next(true);
                this.azureRedirectProcessing.next(false);
                this.router.navigate([this.config.loginRoute || '/login'], {
                  queryParams: { error: 'not_admin' }
                });
              } else {
                console.log('[Azure AD] User authenticated, clearing processing state');
                this.msalInitialized.next(true);
                this.azureRedirectProcessing.next(false);
              }
            },
            error: (error) => {
              console.error('[Azure AD] Backend authentication failed:', error);
              this.msalInitialized.next(true);
              this.azureRedirectProcessing.next(false);
              this.router.navigate([this.config.loginRoute || '/login'], {
                queryParams: { error: 'azure_ad_failed' }
              });
            }
          });
        } else {
          console.log('[Azure AD] No redirect response found');
          this.msalInitialized.next(true);
          this.azureRedirectProcessing.next(false);
        }
      })
      .catch((error) => {
        console.error('[Azure AD] Redirect handling failed:', error);
        this.msalInitialized.next(true);
        this.azureRedirectProcessing.next(false);
        this.router.navigate([this.config.loginRoute || '/login'], {
          queryParams: { error: 'azure_ad_error' }
        });
      });
  }

  logoutAzureAD(): void {
    if (!this.msalInstance) {
      this.logout();
      return;
    }

    const logoutRequest = {
      postLogoutRedirectUri: this.config.azureAD?.postLogoutRedirectUri || window.location.origin
    };

    this.clearAuthState();
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
    }

    this.msalInstance.logoutRedirect(logoutRequest).catch((error) => {
      console.error('[Azure AD] Logout failed:', error);
    });
  }

  isAzureADAuthenticated(): boolean {
    if (!this.msalInstance) return false;
    const accounts = this.msalInstance.getAllAccounts();
    return accounts.length > 0;
  }
}
